import React from "react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./ui/accordion";
import { MapPin, Clock, Users, Luggage, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import { useI18n } from "../i18n";
import { useBooking } from "../lib/booking-context";
import { useCurrency } from "../lib/currency-context";

const PRAGUE_NIGHT =
  "https://images.unsplash.com/photo-1709645499176-4f6a66c47217?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxQcmFndWUlMjBuaWdodCUyMHNreWxpbmUlMjBDaGFybGVzJTIwQnJpZGdlfGVufDB8fHxibGFja3wxNzg4NTQxODczfDA&ixlib=rb-4.1.0&q=85&w=1400";

const ROUTES = [
  { city: "Wien (Vídeň)", km: 333, h: "~4:00", price: 9445 },
  { city: "Dresden (Drážďany)", km: 150, h: "~2:10", price: 4870 },
  { city: "München (Mnichov)", km: 385, h: "~4:50", price: 10745 },
];

const FAQ = {
  ru: [
    { q: "Пересечение границы", a: "Все документы водителя в порядке, границы Шенгена проходим без остановок. Загранпаспорт клиента при себе." },
    { q: "Остановки в пути", a: "Санитарные остановки и кофе-паузы включены. По желанию — остановки для фото у достопримечательностей." },
    { q: "Многодневные поездки", a: "Организуем поездки на несколько дней с ожиданием водителя. Стоимость обсуждается индивидуально." },
  ],
  cs: [
    { q: "Překročení hranic", a: "Doklady řidiče jsou v pořádku, schengenské hranice projíždíme bez zastávek. Klient s sebou cestovní pas." },
    { q: "Zastávky na cestě", a: "Hygienické zastávky a přestávky na kávu jsou v ceně. Na přání i zastávky na fotky u památek." },
    { q: "Vícedenní cesty", a: "Zajišťujeme vícedenní cesty s čekáním řidiče. Cena individuálně." },
  ],
  en: [
    { q: "Border crossing", a: "Driver documents are in order, Schengen borders are crossed without stops. Bring your passport." },
    { q: "Stops en route", a: "Rest and coffee stops are included. On request, photo stops at landmarks." },
    { q: "Multi-day trips", a: "We arrange multi-day trips with the driver waiting. Price agreed individually." },
  ],
};

export const LongDistance = () => {
  const { t, lang } = useI18n();
  const { requestBooking } = useBooking();
  const { fmt } = useCurrency();
  const faq = FAQ[lang] || FAQ.en;

  return (
    <section id="long-distance" className="relative py-16 sm:py-20 lg:py-28 dark-panel overflow-hidden" data-testid="long-distance-section">
      <div className="noise-overlay" />
      <div className="absolute inset-0 opacity-[0.16]" style={{ backgroundImage: `url(${PRAGUE_NIGHT})`, backgroundSize: "cover", backgroundPosition: "center", maskImage: "radial-gradient(65% 65% at 78% 25%, black, transparent)", WebkitMaskImage: "radial-gradient(65% 65% at 78% 25%, black, transparent)" }} />

      <div className="relative max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Editorial left */}
          <div className="lg:col-span-5">
            <span className="eyebrow inline-flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> {t("calc.eyebrow")}</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.02em] leading-[1.05]">
              <span className="gold-text">{t("services.long.name")}</span>
            </h2>
            <p className="mt-4 text-white/70 leading-relaxed">{t("services.long.desc")}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Badge className="bg-accent text-accent-foreground hover:bg-accent rounded-full"><ShieldCheck className="h-3.5 w-3.5 mr-1" /> {t("hero.badge2")}</Badge>
              <Badge variant="outline" className="border-white/20 text-white/80 rounded-full">{t("footer.available")}</Badge>
            </div>
            <div className="gold-rule my-8" />
            <Accordion type="single" collapsible className="w-full" data-testid="long-distance-faq">
              {faq.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-white/12">
                  <AccordionTrigger className="text-left text-white hover:text-accent hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-white/70">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <Button className="mt-6 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 shimmer active:scale-[0.98]" onClick={() => document.getElementById("calculator")?.scrollIntoView({ behavior: "smooth" })} data-testid="long-distance-calc-button">
              {t("cta.calc")} <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>

          {/* Signature route cards right */}
          <div className="lg:col-span-7 space-y-4">
            {ROUTES.map((r, i) => (
              <motion.div
                key={r.city}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="group rounded-2xl border border-white/12 bg-white/[0.04] backdrop-blur-md p-6 hover:border-accent/60 hover:bg-white/[0.07] transition-colors duration-200 flex flex-col sm:flex-row sm:items-center gap-5"
                data-testid={`long-distance-route-${i}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-[0.14em]">
                    <MapPin className="h-3.5 w-3.5 text-accent" /> Praha <ArrowRight className="h-3 w-3" /> <span className="text-white/80">{r.city}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-white/70">
                    <span className="flex items-center gap-1.5"><RouteBullet /> {r.km} km</span>
                    <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {r.h}</span>
                    <span className="flex items-center gap-1.5"><Users className="h-4 w-4" /> 1–4</span>
                    <span className="flex items-center gap-1.5"><Luggage className="h-4 w-4" /> 3–4</span>
                  </div>
                </div>
                <div className="flex items-center justify-between sm:flex-col sm:items-end gap-3">
                  <div className="sm:text-right">
                    <span className="text-white/50 text-xs">{t("routes.from")}</span>
                    <p className="font-display text-2xl font-semibold gold-text tabular-nums">{fmt(r.price)}</p>
                  </div>
                  <Button size="sm" className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 active:scale-[0.98]" onClick={() => requestBooking({ to_location: r.city, from_location: "Praha", category: "long_distance", route: `Praha → ${r.city}`, estimated_price: r.price, distance_km: r.km })} data-testid={`long-distance-book-${i}`}>
                    {t("routes.book")}
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const RouteBullet = () => <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent" />;
